import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { getPassengers, updatePassengerCategory, registerStay, getPointsHistory, deletePassenger, adjustPassengerPoints, voidPointTransaction } from '../services/userService';
import { getSettings } from '../services/settingService';
import { getAllRewards, Reward } from '../services/rewardService';
import { requestRewardRedemption } from '../services/rewardRedemptionService';
import { Search, ChevronLeft, ChevronRight, Mail, Calendar, DollarSign, History, Gift, Trash2, Pencil, RotateCcw } from 'lucide-react';
import { message, Modal, InputNumber, Select } from 'antd';

interface UserData {
  dni?: string;
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  birth_date?: string;
}

interface Passenger {
  id: number;
  name: string;
  last_name: string;
  email: string;
  category: 'classic' | 'premium' | 'silver';
  created_at: string;
  user_data?: UserData;
  points?: {
    total_points: number;
  };
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

const PassengersPage: React.FC = () => {
  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stayModalVisible, setStayModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [redeemModalVisible, setRedeemModalVisible] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState<Passenger | null>(null);
  const [stayAmount, setStayAmount] = useState<number>(0);
  const [stayDescription, setStayDescription] = useState('');
  const [pointsPerAmount, setPointsPerAmount] = useState<number>(10);
  const [amountForPoints, setAmountForPoints] = useState<number>(50000);
  const [pointsHistory, setPointsHistory] = useState<any[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [selectedReward, setSelectedReward] = useState<number | null>(null);
  const [redeemNotes, setRedeemNotes] = useState('');
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjustPointsValue, setAdjustPointsValue] = useState<number | null>(null);
  const [adjustReason, setAdjustReason] = useState('');
  const [voidTarget, setVoidTarget] = useState<any | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    from: 0,
    to: 0,
  });

  const fetchPassengers = async (page: number = 1, search: string = '') => {
    setLoading(true);
    try {
      const response = await getPassengers(page, 15, search);
      setPassengers(response.data);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
        per_page: response.per_page,
        total: response.total,
        from: response.from,
        to: response.to,
      });
    } catch (error) {
      message.error('Error al cargar los huéspedes');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      const settings = response.data || response;

      const pointsPerAmountSetting = settings.find((s: any) => s.key === 'points_per_amount');
      const amountForPointsSetting = settings.find((s: any) => s.key === 'amount_for_points');

      if (pointsPerAmountSetting) setPointsPerAmount(pointsPerAmountSetting.value);
      if (amountForPointsSetting) setAmountForPoints(amountForPointsSetting.value);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  useEffect(() => {
    fetchPassengers();
    fetchSettings();
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const data = await getAllRewards();
      setRewards(data.filter(r => r.is_active));
    } catch (error) {
      console.error('Error loading rewards:', error);
    }
  };

  const handleSearch = () => {
    fetchPassengers(1, searchTerm);
  };

  const handlePageChange = (newPage: number) => {
    fetchPassengers(newPage, searchTerm);
  };

  const handleCategoryChange = async (passengerId: number, newCategory: string) => {
    try {
      await updatePassengerCategory(passengerId, newCategory);
      message.success('Categoría actualizada correctamente');
      fetchPassengers(pagination.current_page, searchTerm);
    } catch (error) {
      message.error('Error al actualizar la categoría');
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'premium':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'silver':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'premium':
        return 'Premium';
      case 'silver':
        return 'Silver';
      default:
        return 'Classic';
    }
  };

  const handleOpenStayModal = (passenger: Passenger) => {
    setSelectedPassenger(passenger);
    setStayAmount(0);
    setStayDescription('Estadía en Hotel Chiavari');
    setStayModalVisible(true);
  };

  const handleCloseStayModal = () => {
    setStayModalVisible(false);
    setSelectedPassenger(null);
    setStayAmount(0);
    setStayDescription('');
  };

  const calculatePoints = (amount: number): number => {
    // Usar la configuración de settings: (monto / amountForPoints) * pointsPerAmount
    return Math.floor((amount / amountForPoints) * pointsPerAmount);
  };

  const handleRegisterStay = async () => {
    if (!selectedPassenger) return;

    if (stayAmount < amountForPoints) {
      message.error(`El monto mínimo es $${amountForPoints.toLocaleString('es-AR')}`);
      return;
    }

    try {
      const result = await registerStay(selectedPassenger.id, stayAmount, stayDescription);
      message.success(`Estadía registrada. Se asignaron ${result.data.points_earned} puntos`);
      handleCloseStayModal();
      fetchPassengers(pagination.current_page, searchTerm);
    } catch (error) {
      message.error('Error al registrar la estadía');
    }
  };

  const getErrorMessage = (error: any, fallback: string): string =>
    error?.response?.data?.message || fallback;

  const getReasonError = (reason: string): string | null => {
    if (!reason.trim()) return 'El motivo es obligatorio';
    if (reason.trim().length > 200) return 'El motivo no puede superar los 200 caracteres';
    return null;
  };

  const loadPointsHistory = async (passengerId: number) => {
    try {
      const response = await getPointsHistory(passengerId);
      setPointsHistory(response.data || []);
    } catch (error) {
      message.error('Error al cargar el historial de puntos');
      setPointsHistory([]);
    }
  };

  const handleOpenHistoryModal = async (passenger: Passenger) => {
    setSelectedPassenger(passenger);
    setHistoryModalVisible(true);
    await loadPointsHistory(passenger.id);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalVisible(false);
    setSelectedPassenger(null);
    setPointsHistory([]);
  };

  const handleDeletePassenger = (passenger: Passenger) => {
    Modal.confirm({
      title: 'Eliminar huésped',
      content: `Se eliminará a ${passenger.name} ${passenger.last_name} junto con sus puntos, historial, favoritos, familiares y canjes. Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deletePassenger(passenger.id);
          message.success('Huésped eliminado correctamente');
          // Si era el último de la página, volver a la anterior
          const page = passengers.length === 1 && pagination.current_page > 1
            ? pagination.current_page - 1
            : pagination.current_page;
          fetchPassengers(page, searchTerm);
        } catch (error) {
          message.error(getErrorMessage(error, 'Error al eliminar el huésped'));
        }
      },
    });
  };

  // Tras un ajuste o anulación: actualizar total visible, historial y listado
  const refreshAfterCorrection = async (passenger: Passenger, totalPoints: number) => {
    setSelectedPassenger({ ...passenger, points: { total_points: totalPoints } });
    fetchPassengers(pagination.current_page, searchTerm);
    await loadPointsHistory(passenger.id);
  };

  const handleOpenAdjustModal = () => {
    setAdjustPointsValue(null);
    setAdjustReason('');
    setAdjustModalVisible(true);
  };

  const handleAdjustPoints = async () => {
    if (!selectedPassenger) return;

    if (!adjustPointsValue) {
      message.error('Ingresá una cantidad de puntos distinta de 0');
      return;
    }
    const reasonError = getReasonError(adjustReason);
    if (reasonError) {
      message.error(reasonError);
      return;
    }

    setSavingCorrection(true);
    try {
      const result = await adjustPassengerPoints(selectedPassenger.id, adjustPointsValue, adjustReason.trim());
      message.success('Puntos ajustados correctamente');
      setAdjustModalVisible(false);
      await refreshAfterCorrection(selectedPassenger, result.data.total_points);
    } catch (error) {
      message.error(getErrorMessage(error, 'Error al ajustar los puntos'));
    } finally {
      setSavingCorrection(false);
    }
  };

  const handleOpenVoidModal = (transaction: any) => {
    setVoidTarget(transaction);
    setVoidReason('');
  };

  const handleVoidTransaction = async () => {
    if (!selectedPassenger || !voidTarget) return;

    const reasonError = getReasonError(voidReason);
    if (reasonError) {
      message.error(reasonError);
      return;
    }

    setSavingCorrection(true);
    try {
      const result = await voidPointTransaction(selectedPassenger.id, voidTarget.id, voidReason.trim());
      message.success('Transacción anulada correctamente');
      setVoidTarget(null);
      await refreshAfterCorrection(selectedPassenger, result.data.total_points);
    } catch (error) {
      message.error(getErrorMessage(error, 'Error al anular la transacción'));
    } finally {
      setSavingCorrection(false);
    }
  };

  const handleOpenRedeemModal = (passenger: Passenger) => {
    setSelectedPassenger(passenger);
    setSelectedReward(null);
    setRedeemNotes('');
    setRedeemModalVisible(true);
  };

  const handleCloseRedeemModal = () => {
    setRedeemModalVisible(false);
    setSelectedPassenger(null);
    setSelectedReward(null);
    setRedeemNotes('');
  };

  const printCoupon = (redemptionData: any) => {
    const reward = rewards.find(r => r.id === selectedReward);
    if (!reward || !selectedPassenger) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.warning('No se pudo abrir la ventana de impresión. Verifica los permisos del navegador.');
      return;
    }

    const couponHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cupón de Canje - ${redemptionData.redemption_code}</title>
          <style>
            @media print {
              body { margin: 0; }
              @page { margin: 1cm; }
            }
            body {
              font-family: Arial, sans-serif;
              max-width: 400px;
              margin: 20px auto;
              padding: 20px;
              border: 2px dashed #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              color: #1a56db;
            }
            .header h2 {
              margin: 5px 0 0 0;
              font-size: 14px;
              color: #666;
              font-weight: normal;
            }
            .code-section {
              text-align: center;
              background: #f3f4f6;
              padding: 20px;
              margin: 20px 0;
              border-radius: 8px;
            }
            .code {
              font-size: 28px;
              font-weight: bold;
              letter-spacing: 3px;
              color: #1a56db;
              font-family: 'Courier New', monospace;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 10px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .info-label {
              font-weight: bold;
              color: #374151;
            }
            .info-value {
              color: #6b7280;
              text-align: right;
            }
            .reward-name {
              font-size: 18px;
              font-weight: bold;
              color: #059669;
              text-align: center;
              margin: 20px 0;
              padding: 15px;
              background: #d1fae5;
              border-radius: 8px;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 15px;
              border-top: 2px solid #333;
              font-size: 11px;
              color: #666;
            }
            .valid-until {
              text-align: center;
              margin-top: 15px;
              font-size: 12px;
              color: #ef4444;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Hotel Chiavari</h1>
            <h2>Cupón de Canje de Premio</h2>
          </div>

          <div class="reward-name">
            ${reward.name}
          </div>

          <div class="code-section">
            <div style="font-size: 12px; color: #666; margin-bottom: 5px;">CÓDIGO DE CANJE</div>
            <div class="code">${redemptionData.redemption_code}</div>
          </div>

          <div style="margin: 20px 0;">
            <div class="info-row">
              <span class="info-label">Huésped:</span>
              <span class="info-value">${selectedPassenger.name} ${selectedPassenger.last_name}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Email:</span>
              <span class="info-value">${selectedPassenger.email}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Puntos Utilizados:</span>
              <span class="info-value">${reward.points_cost} puntos</span>
            </div>
            <div class="info-row">
              <span class="info-label">Fecha de Emisión:</span>
              <span class="info-value">${new Date().toLocaleDateString('es-AR')} ${new Date().toLocaleTimeString('es-AR')}</span>
            </div>
          </div>

          ${reward.description ? `
            <div style="margin: 20px 0; padding: 15px; background: #fef3c7; border-radius: 8px; font-size: 13px;">
              <strong>Descripción:</strong><br/>
              ${reward.description}
            </div>
          ` : ''}

          ${redeemNotes ? `
            <div style="margin: 15px 0; font-size: 12px; color: #666;">
              <strong>Notas:</strong> ${redeemNotes}
            </div>
          ` : ''}

          <div class="valid-until">
            Válido por 30 días desde la fecha de emisión
          </div>

          <div class="footer">
            <p>Presente este cupón en recepción para canjear su premio.</p>
            <p>Hotel Chiavari - San Bernardo</p>
            <p style="margin-top: 10px; font-size: 10px;">Impreso el ${new Date().toLocaleString('es-AR')}</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(couponHTML);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleRedeemReward = async () => {
    if (!selectedPassenger || !selectedReward) {
      message.error('Debe seleccionar un premio');
      return;
    }

    const reward = rewards.find(r => r.id === selectedReward);
    if (!reward) return;

    const userPoints = selectedPassenger.points?.total_points || 0;
    if (userPoints < reward.points_cost) {
      message.error(`El huésped no tiene suficientes puntos. Requiere ${reward.points_cost} puntos y tiene ${userPoints}`);
      return;
    }

    try {
      const result = await requestRewardRedemption({
        userId: selectedPassenger.id,
        rewardId: selectedReward,
        notes: redeemNotes,
      });

      message.success(`Premio "${reward.name}" canjeado exitosamente. Se descontaron ${reward.points_cost} puntos`);

      // Imprimir cupón automáticamente
      if (result.data) {
        printCoupon(result.data);
      }

      handleCloseRedeemModal();
      fetchPassengers(pagination.current_page, searchTerm);
    } catch (error) {
      message.error('Error al canjear el premio');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Huéspedes</h1>
        <p className="text-sm text-slate-600 mt-1">Gestión de pasajeros y categorías</p>
      </div>

      <Card>
        <div className="flex gap-3 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Buscar por nombre, apellido, email o DNI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button onClick={handleSearch}>
            <Search size={18} className="mr-2" />
            Buscar
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando huéspedes...</div>
        ) : passengers.length === 0 ? (
          <div className="text-center py-12 text-slate-500">No se encontraron huéspedes</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Huésped</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Contacto</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Puntos</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Categoría</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Fecha Registro</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {passengers.map((passenger) => (
                    <tr key={passenger.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {passenger.name} {passenger.last_name}
                          </p>
                          {passenger.user_data?.dni && (
                            <p className="text-xs text-slate-500">DNI: {passenger.user_data.dni}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail size={14} />
                            {passenger.email}
                          </div>
                          {passenger.user_data?.phone && (
                            <p className="text-xs text-slate-500">Tel: {passenger.user_data.phone}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-center">
                          <p className="text-lg font-bold text-blue-600">
                            {passenger.points?.total_points || 0}
                          </p>
                          <p className="text-xs text-slate-500">puntos</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(passenger.category)}`}>
                          {getCategoryLabel(passenger.category)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar size={14} />
                          {new Date(passenger.created_at).toLocaleDateString('es-AR')}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <select
                            value={passenger.category}
                            onChange={(e) => handleCategoryChange(passenger.id, e.target.value)}
                            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          >
                            <option value="classic">Classic</option>
                            <option value="silver">Silver</option>
                            <option value="premium">Premium</option>
                          </select>
                          <Button
                            size="sm"
                            onClick={() => handleOpenStayModal(passenger)}
                            className="whitespace-nowrap"
                          >
                            <DollarSign size={14} className="mr-1" />
                            Estadía
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenHistoryModal(passenger)}
                            className="whitespace-nowrap"
                          >
                            <History size={14} className="mr-1" />
                            Historial
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleOpenRedeemModal(passenger)}
                            className="whitespace-nowrap bg-green-600 hover:bg-green-700"
                          >
                            <Gift size={14} className="mr-1" />
                            Canjear
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeletePassenger(passenger)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
              <div className="text-sm text-slate-600">
                Mostrando {pagination.from} a {pagination.to} de {pagination.total} huéspedes
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  <ChevronLeft size={16} />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.last_page <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.last_page - 2) {
                      pageNum = pagination.last_page - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          pagination.current_page === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Modal de Registro de Estadía */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <DollarSign size={20} className="text-blue-600" />
            <span>Registrar Estadía</span>
          </div>
        }
        open={stayModalVisible}
        onOk={handleRegisterStay}
        onCancel={handleCloseStayModal}
        okText="Registrar"
        cancelText="Cancelar"
        okButtonProps={{ className: 'bg-blue-600' }}
        width={500}
      >
        {selectedPassenger && (
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">Pasajero</p>
              <p className="font-semibold text-slate-900">
                {selectedPassenger.name} {selectedPassenger.last_name}
              </p>
              <p className="text-sm text-slate-500">{selectedPassenger.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Monto de la Estadía ($)
              </label>
              <InputNumber
                value={stayAmount}
                onChange={(value) => setStayAmount(value || 0)}
                min={0}
                prefix="$"
                style={{ width: '100%' }}
                size="large"
                placeholder="Ingrese el monto"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Descripción (Opcional)
              </label>
              <Input
                value={stayDescription}
                onChange={(e) => setStayDescription(e.target.value)}
                placeholder="Ej: Estadía del 1 al 5 de febrero"
              />
            </div>

            {stayAmount >= amountForPoints && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">Puntos a asignar:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {calculatePoints(stayAmount)} puntos
                  </span>
                </div>
                <p className="text-xs text-blue-700 mt-1">
                  {pointsPerAmount} puntos por cada ${amountForPoints.toLocaleString('es-AR')} de estadía
                </p>
              </div>
            )}

            {stayAmount > 0 && stayAmount < amountForPoints && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  El monto mínimo para ganar puntos es ${amountForPoints.toLocaleString('es-AR')}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Historial de Puntos */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <History size={20} className="text-blue-600" />
            <span>Historial de Puntos</span>
          </div>
        }
        open={historyModalVisible}
        onCancel={handleCloseHistoryModal}
        footer={null}
        width={700}
      >
        {selectedPassenger && (
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pasajero</p>
                  <p className="font-semibold text-slate-900">
                    {selectedPassenger.name} {selectedPassenger.last_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Puntos Totales</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {selectedPassenger.points?.total_points || 0}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleOpenAdjustModal}
                    className="mt-2 whitespace-nowrap"
                  >
                    <Pencil size={14} className="mr-1" />
                    Ajustar puntos
                  </Button>
                </div>
              </div>
            </div>

            {pointsHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No hay transacciones registradas
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {pointsHistory.map((transaction: any) => (
                  <div
                    key={transaction.id}
                    className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${transaction.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {transaction.points >= 0 ? '+' : ''}{transaction.points}
                          </span>
                          <span className="text-sm text-slate-500">puntos</span>
                        </div>
                        <p className={`text-sm font-medium mt-1 ${transaction.is_voided ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {transaction.description || transaction.action?.description || 'Sin descripción'}
                        </p>
                        {transaction.is_voided && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            Anulada
                          </span>
                        )}
                        {transaction.amount && (
                          <p className="text-xs text-slate-600 mt-1">
                            Monto: ${transaction.amount.toLocaleString('es-AR')}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        {new Date(transaction.created_at).toLocaleDateString('es-AR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {transaction.can_void && (
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenVoidModal(transaction)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <RotateCcw size={12} className="mr-1" />
                              Anular
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Canje de Premio */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Gift size={20} className="text-green-600" />
            <span>Canjear Premio</span>
          </div>
        }
        open={redeemModalVisible}
        onOk={handleRedeemReward}
        onCancel={handleCloseRedeemModal}
        okText="Canjear"
        cancelText="Cancelar"
        okButtonProps={{ className: 'bg-green-600' }}
        width={600}
      >
        {selectedPassenger && (
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Huésped</p>
                  <p className="font-semibold text-slate-900">
                    {selectedPassenger.name} {selectedPassenger.last_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Puntos Disponibles</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {selectedPassenger.points?.total_points || 0}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Seleccionar Premio
              </label>
              <Select
                value={selectedReward}
                onChange={(value) => setSelectedReward(value)}
                style={{ width: '100%' }}
                size="large"
                placeholder="Seleccione un premio"
              >
                {rewards.map((reward) => {
                  const userPoints = selectedPassenger.points?.total_points || 0;
                  const hasEnoughPoints = userPoints >= reward.points_cost;

                  return (
                    <Select.Option
                      key={reward.id}
                      value={reward.id}
                      disabled={!hasEnoughPoints}
                    >
                      <div className="flex items-center justify-between">
                        <span className={!hasEnoughPoints ? 'text-slate-400' : ''}>
                          {reward.name}
                        </span>
                        <span className={`font-semibold ${!hasEnoughPoints ? 'text-red-500' : 'text-green-600'}`}>
                          {reward.points_cost} puntos
                        </span>
                      </div>
                    </Select.Option>
                  );
                })}
              </Select>
            </div>

            {selectedReward && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                {(() => {
                  const reward = rewards.find(r => r.id === selectedReward);
                  if (!reward) return null;
                  return (
                    <div>
                      <p className="text-sm font-medium text-green-900 mb-2">
                        {reward.name}
                      </p>
                      <p className="text-sm text-green-700 mb-3">
                        {reward.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-800">Costo:</span>
                        <span className="text-lg font-bold text-green-600">
                          {reward.points_cost} puntos
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Notas (Opcional)
              </label>
              <Input
                value={redeemNotes}
                onChange={(e) => setRedeemNotes(e.target.value)}
                placeholder="Ej: Cliente solicitó canje en recepción"
              />
            </div>

            {rewards.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No hay premios activos disponibles
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Ajuste Manual de Puntos */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Pencil size={20} className="text-blue-600" />
            <span>Ajustar Puntos</span>
          </div>
        }
        open={adjustModalVisible}
        onOk={handleAdjustPoints}
        onCancel={() => setAdjustModalVisible(false)}
        okText="Guardar ajuste"
        cancelText="Cancelar"
        okButtonProps={{ className: 'bg-blue-600', loading: savingCorrection }}
        width={500}
      >
        {selectedPassenger && (() => {
          const currentPoints = selectedPassenger.points?.total_points || 0;
          const newTotal = currentPoints + (adjustPointsValue || 0);
          return (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Usá un número positivo para sumar puntos o negativo para restar. El ajuste queda registrado en el historial con el motivo.
              </p>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Puntos a sumar / restar
                </label>
                <InputNumber
                  value={adjustPointsValue}
                  onChange={(value) => setAdjustPointsValue(value)}
                  precision={0}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder="Ej: 50 o -50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Motivo
                </label>
                <Input
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Ej: Estadía cargada con monto incorrecto"
                />
              </div>

              <div className={`p-4 rounded-lg border ${newTotal < 0 ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">Total actual: {currentPoints}</span>
                  <span className={`text-lg font-bold ${newTotal < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                    Nuevo total: {newTotal}
                  </span>
                </div>
                {newTotal < 0 && (
                  <p className="text-xs text-red-700 mt-1">El total no puede quedar negativo.</p>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Modal de Anulación de Transacción */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <RotateCcw size={20} className="text-red-600" />
            <span>Anular Transacción</span>
          </div>
        }
        open={!!voidTarget}
        onOk={handleVoidTransaction}
        onCancel={() => setVoidTarget(null)}
        okText="Anular"
        okType="danger"
        cancelText="Cancelar"
        okButtonProps={{ loading: savingCorrection }}
        width={500}
      >
        {voidTarget && (
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-slate-900">
                {voidTarget.description || voidTarget.action?.description || 'Sin descripción'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {new Date(voidTarget.created_at).toLocaleString('es-AR')}
              </p>
            </div>

            <p className="text-sm text-slate-700">
              Se {voidTarget.points >= 0 ? 'restarán' : 'sumarán'} <strong>{Math.abs(voidTarget.points)} puntos</strong> al huésped.
              La transacción original queda en el historial marcada como anulada.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Motivo
              </label>
              <Input
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Ej: Estadía cargada dos veces"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PassengersPage;
